import Collapse from "@mui/material/Collapse"
import List from "@mui/material/List"
import ListItemButton from "@mui/material/ListItemButton"
import ListItemText from "@mui/material/ListItemText"
import Stack from "@mui/material/Stack"
import * as React from "react"
import { CustomButton } from "@src/components/Button"
import Icons from "@src/components/Icons"
import { getClassNameActive } from "../config"

import { styled } from "@mui/system"
import { useTranslation } from "react-i18next"

const SubMenuItem = styled(ListItemButton)(({ theme, level = 0 }) => ({
  color: "#141414 !important",
  fontSize: 14,
  fontWeight: 400,
  lineHeight: "24px",
  padding: `0 ${48 + (level * 16)}px`,
  minHeight: "32px !important",
  width: "calc(100% + 16px)",
  minWidth: 200,
  borderTop: "1px solid #E5E5E5",
  marginLeft: "-16px !important",
  textTransform: "uppercase",
  "&.active": {
    color: "#000 !important",
    backgroundColor: "#F6F6F6 !important"
  }
}))

const NestedSubMenuItem = styled(ListItemButton)(({ theme, level = 0 }) => ({
  color: "#141414 !important",
  fontSize: 14,
  fontWeight: 400,
  lineHeight: "24px",
  padding: `0 25px`,
  minHeight: "32px !important",
  width: "calc(100% + 16px)",
  minWidth: 200,
  borderTop: "1px solid #E5E5E5",
  textTransform: "uppercase",
  "&.active": {
    color: "#000 !important",
    backgroundColor: "#F6F6F6 !important"
  }
}))

// Recursive component for nested sub-menus
const NestedSubMenu = ({ item, currentPath, onRedirect, level = 0 }) => {
  const { t } = useTranslation()
  const [open, setOpen] = React.useState(false)

  const handleClick = () => {
    setOpen(!open)
  }

  // If item has subMenu, render as expandable item
  if (item.subMenu && item.subMenu.length > 0) {
    return (
      <Stack
        sx={{
          width: "100%",
          flexDirection: "column",
          overflow: "hidden"
        }}
      >
        <NestedSubMenuItem
          className={getClassNameActive(item, currentPath)}
          onClick={handleClick}
          level={level}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          <ListItemText
            sx={{ textTransform: "uppercase" }}
            primary={item.key ? t(item.key) : item.title}
          />
          {open ? <Icons name="arrow-down" /> : <Icons name="arrow-up" />}
        </NestedSubMenuItem>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.subMenu.map((subItem, index) => (
              <NestedSubMenu
                key={index}
                item={subItem}
                currentPath={currentPath}
                onRedirect={onRedirect}
                level={level + 1}
              />
            ))}
          </List>
        </Collapse>
      </Stack>
    )
  }

  // If item doesn't have subMenu, render as regular item
  return (
    <SubMenuItem
      className={getClassNameActive(item, currentPath)}
      onClick={onRedirect(item.path)}
      level={level}
    >
      <ListItemText
        sx={{ textTransform: "uppercase" }}
        primary={item.key ? t(item.key) : item.title}
      />
    </SubMenuItem>
  )
}

const SideNavWithSubMenu = props => {
  const { currentPath, data, onRedirect } = props || { data: {} }
  const { t } = useTranslation()
  const [open, setOpen] = React.useState(false)

  const handleClick = () => {
    // Mobile: parent with submenu expands only; children handle navigation
    setOpen(prev => !prev)
  }

  return (
    <Stack
      sx={{
        width: "100%",
        borderBottom: "1px solid #E5E5E5",
        flexDirection: "column",
        overflow: "hidden"
      }}
    >
      <CustomButton
        disableRipple
        label={t(data.key)}
        sx={{
          backgroundColor: "transparent !important",
          border: "none !important",
          color: "#141414 !important",
          padding: "0 16px !important",
          minHeight: "auto !important",
          height: "32px !important",
          width: "100%",
          display: "flex",
          justifyContent: "flex-start !important",
          outline: 0,
          borderRadius: "0 !important",
          "&.active": {
            color: "#000 !important",
            backgroundColor: "#F6F6F6 !important"
          },
          "& .label": {
            fontSize: "14px"
          }
        }}
        endIcon={open ? <Icons name="arrow-down" /> : <Icons name="arrow-up" />}
        className={getClassNameActive(data, currentPath)}
        onClick={handleClick}
      />

      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {data.subMenu.map((item, index) => (
            <NestedSubMenu
              key={index}
              item={item}
              currentPath={currentPath}
              onRedirect={onRedirect}
              level={0}
            />
          ))}
        </List>
      </Collapse>
    </Stack>
  )
}

export default SideNavWithSubMenu
